import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config';

const COULEUR_DEFAUT = '#B0B0B0';
const TOUT_APPELS_MAX = 20;

const styles = {
  voile: {
    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
    backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9998, display: 'flex',
    alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: 16, boxSizing: 'border-box',
  },
  modal: {
    background: '#1a1025', borderRadius: 12, border: '1px solid #4a3065', width: '100%', maxWidth: 640,
    maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', color: '#fff',
  },
  tete: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '18px 22px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  sousTitre: { margin: '2px 0 0', fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)' },
  corps: { padding: '12px 22px', overflowY: 'auto', flex: 1 },
  pied: { padding: '14px 22px 18px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: 8 },
  bouton: { padding: '8px 14px', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap' },
  lien: { background: 'none', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: '0.75rem' },
};

function bouton(type, desactive) {
  const fonds = { primaire: '#9C27B0', secondaire: 'rgba(255,255,255,0.1)', danger: '#b26a00' };
  return { ...styles.bouton, background: fonds[type], opacity: desactive ? 0.45 : 1, cursor: desactive ? 'not-allowed' : 'pointer' };
}

function dateCourte(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const deux = (n) => String(n).padStart(2, '0');
  return `${deux(d.getDate())}/${deux(d.getMonth() + 1)} à ${deux(d.getHours())}:${deux(d.getMinutes())}`;
}

function estTransferable(ligne) {
  return ligne.status === 'PENDING' || ligne.status === 'FAILED';
}

export default function InventaireRoue({ ouvert, onFermer, sessionToken, onNombre, icones, raretes, couleurs }) {
  const [lignes, setLignes] = useState([]);
  const [detection, setDetection] = useState({ connecte: false, personnages: [] });
  const [chargement, setChargement] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [caseCochee, setCaseCochee] = useState(false);
  const [message, setMessage] = useState(null);
  const [livres, setLivres] = useState([]);
  const verrou = useRef(false);

  const charger = useCallback(async () => {
    if (!sessionToken) return;
    setChargement(true);
    try {
      const res = await fetch(`${API_URL}/token-wheel/inventory`, { headers: { Authorization: `Bearer ${sessionToken}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Impossible de charger votre inventaire.');
      const liste = Array.isArray(data.inventory) ? data.inventory.filter((l) => l.status !== 'DELIVERED') : [];
      setLignes(liste);
      if (data.detection) setDetection(data.detection);
      if (onNombre) onNombre(liste.filter(estTransferable).length);
    } catch (err) {
      setMessage({ type: 'echec', texte: err.message || 'Erreur réseau lors du chargement de l\'inventaire.' });
    } finally {
      setChargement(false);
    }
  }, [sessionToken, onNombre]);

  useEffect(() => {
    if (ouvert) {
      setMessage(null);
      setLivres([]);
      setConfirmation(null);
      charger();
    }
  }, [ouvert, charger]);

  useEffect(() => {
    if (!ouvert) return undefined;
    const touche = (e) => { if (e.key === 'Escape' && !verrou.current) onFermer(); };
    document.addEventListener('keydown', touche);
    return () => document.removeEventListener('keydown', touche);
  }, [ouvert, onFermer]);

  const appeler = useCallback(async (corps) => {
    const res = await fetch(`${API_URL}/token-wheel/transfer`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(corps),
    });
    const data = await res.json().catch(() => ({}));
    return { res, data };
  }, [sessionToken]);

  const envoyer = useCallback(async (demande, confirme) => {
    if (verrou.current) return;
    verrou.current = true;
    setEnCours(true);
    setMessage(null);
    setLignes((prev) => prev.map((l) => (
      estTransferable(l) && (demande.tout || l.id === demande.deliveryId) ? { ...l, status: 'IN_PROGRESS', enVerification: false } : l
    )));
    const corps = demande.tout ? { tout: true } : { deliveryId: demande.deliveryId };
    if (confirme) corps.confirmationHorsLigne = true;
    const bilan = { livres: 0, echecs: 0, incertains: 0, ignores: 0, perso: null, dernier: null };
    try {
      let appels = 0;
      for (;;) {
        appels++;
        const { res, data } = await appeler(corps);
        if (data.detection) setDetection(data.detection);
        if (res.status === 409 && data.code === 'CONFIRMATION_REQUISE') {
          setConfirmation({ ...demande, etape: 1 });
          setCaseCochee(false);
          return;
        }
        if (!res.ok || !data.success) {
          setMessage({ type: 'echec', texte: data.message || 'Le transfert n\'a pas pu être lancé.' });
          return;
        }
        bilan.livres += data.livres || 0;
        bilan.echecs += data.echecs || 0;
        bilan.incertains += data.incertains || 0;
        bilan.ignores += data.ignores || 0;
        (data.resultats || []).forEach((r) => {
          bilan.dernier = r;
          if (r.statut === 'DELIVERED') {
            setLivres((prev) => (prev.some((x) => x.id === r.deliveryId) ? prev : [...prev, { id: r.deliveryId, itemName: r.itemName, playerName: r.playerName }]));
          }
          if (r.statut === 'DELIVERED' && r.playerName) bilan.perso = r.playerName;
        });
        const continuer = demande.tout && !data.interrompu && data.restant > 0 && (data.livres || 0) > 0 && appels < TOUT_APPELS_MAX;
        if (!continuer) break;
      }
      const dest = bilan.perso ? ` à ${bilan.perso}` : '';
      if (!demande.tout) {
        const r = bilan.dernier;
        if (!r) setMessage({ type: 'echec', texte: 'Aucune réponse exploitable du serveur.' });
        else if (r.statut === 'DELIVERED') setMessage({ type: 'succes', texte: `${r.itemName} a été transféré${dest}.` });
        else if (r.statut === 'SKIPPED') setMessage({ type: 'mixte', texte: r.message });
        else setMessage({ type: r.statut === 'UNCERTAIN' ? 'mixte' : 'echec', texte: `${r.itemName} : ${r.message}` });
      } else if (bilan.livres > 0 && bilan.echecs === 0 && bilan.incertains === 0) {
        setMessage({ type: 'succes', texte: `${bilan.livres} objet(s) transféré(s)${dest}.` });
      } else if (bilan.livres === 0 && bilan.echecs > 0 && bilan.incertains === 0) {
        setMessage({ type: 'echec', texte: `Transfert interrompu : ${bilan.dernier ? bilan.dernier.message : 'échec'}` });
      } else if (bilan.livres === 0 && bilan.echecs === 0 && bilan.incertains === 0) {
        setMessage({ type: 'mixte', texte: 'Aucun objet à transférer : ils sont déjà en cours de transfert ou livrés.' });
      } else {
        const parts = [`${bilan.livres} transféré(s)${dest}`];
        if (bilan.echecs) parts.push(`${bilan.echecs} en échec (toujours dans l'inventaire)`);
        if (bilan.incertains) parts.push(`${bilan.incertains} en vérification (le jeu n'a pas répondu)`);
        setMessage({ type: 'mixte', texte: parts.join(', ') + '.' });
      }
    } catch (_) {
      setMessage({ type: 'mixte', texte: 'La réponse du serveur n\'a pas été reçue. L\'état réel de votre inventaire a été rechargé : un objet ne peut pas être envoyé deux fois.' });
    } finally {
      verrou.current = false;
      setEnCours(false);
      charger();
    }
  }, [appeler, charger]);

  const transferer = useCallback((demande) => {
    if (verrou.current || enCours) return;
    setMessage(null);
    if (!detection.connecte) {
      setConfirmation({ ...demande, etape: 1 });
      setCaseCochee(false);
      return;
    }
    envoyer(demande, false);
  }, [detection.connecte, enCours, envoyer]);

  const actualiser = useCallback(async () => {
    await charger();
  }, [charger]);

  useEffect(() => {
    if (confirmation && detection.connecte) setConfirmation(null);
  }, [confirmation, detection.connecte]);

  if (!ouvert) return null;

  const transferables = lignes.filter(estTransferable);
  const perso = detection.personnages && detection.personnages[0];
  const nomDetecte = detection.personnages && detection.personnages.length > 0
    ? detection.personnages.map((p) => p.name).join(', ')
    : '';
  const quoi = confirmation && confirmation.tout ? `les ${transferables.length} objets de votre inventaire` : 'cet objet';

  const etatLigne = (l) => {
    if (l.status === 'FAILED') {
      return <div style={{ fontSize: '0.72rem', marginTop: 4, color: '#ff8a80', lineHeight: 1.4 }}>Échec : {l.raisonEchec || 'le serveur de jeu a refusé l\'envoi'}. L'objet est toujours dans votre inventaire, vous pouvez réessayer.</div>;
    }
    if (l.status === 'IN_PROGRESS' && l.enVerification) {
      return <div style={{ fontSize: '0.72rem', marginTop: 4, color: '#FFD54F', lineHeight: 1.4 }}>Le jeu n'a pas répondu : transfert en vérification par l'équipe. Il ne sera pas renvoyé en double.</div>;
    }
    if (l.status === 'IN_PROGRESS') {
      return <div style={{ fontSize: '0.72rem', marginTop: 4, color: '#ce93d8' }}>Transfert en cours...</div>;
    }
    return null;
  };

  const boutonLigne = (l) => {
    if (l.status === 'PENDING' || l.status === 'FAILED') {
      const desactive = enCours || !!confirmation;
      return (
        <button type="button" disabled={desactive} style={bouton('primaire', desactive)} onClick={() => transferer({ tout: false, deliveryId: l.id })}>
          {l.status === 'FAILED' ? 'Réessayer' : 'Transférer'}
        </button>
      );
    }
    if (l.status === 'IN_PROGRESS' && !l.enVerification) {
      return <button type="button" disabled style={bouton('primaire', true)}>En cours</button>;
    }
    return null;
  };

  const fondMessage = {
    succes: { background: 'rgba(102,187,106,0.12)', border: '1px solid rgba(102,187,106,0.4)' },
    echec: { background: 'rgba(239,83,80,0.12)', border: '1px solid rgba(239,83,80,0.45)' },
    mixte: { background: 'rgba(255,179,0,0.1)', border: '1px solid rgba(255,179,0,0.45)' },
  };

  return (
    <div style={styles.voile} onClick={(e) => { if (e.target === e.currentTarget && !verrou.current) onFermer(); }}>
      <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="inventaire-roue-titre">
        <div style={styles.tete}>
          <div>
            <h3 id="inventaire-roue-titre" style={{ margin: 0, fontSize: '1.1rem' }}>Inventaire de la roue</h3>
            <p style={styles.sousTitre}>Vos gains en attente d'envoi vers le jeu. Ils restent ici tant qu'ils ne sont pas livrés.</p>
          </div>
          <button type="button" onClick={onFermer} disabled={enCours} style={bouton('secondaire', enCours)}>Fermer</button>
        </div>

        <div style={{
          margin: '14px 22px 0', padding: '10px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.85rem',
          ...(detection.connecte
            ? { background: 'rgba(102,187,106,0.1)', border: '1px solid rgba(102,187,106,0.35)' }
            : { background: 'rgba(255,179,0,0.1)', border: '1px solid rgba(255,179,0,0.45)' }),
        }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: detection.connecte ? '#66BB6A' : '#FFB300' }} />
          <span style={{ flex: 1, lineHeight: 1.4 }}>
            {detection.connecte ? (
              <>Personnage connecté détecté : <strong>{nomDetecte}</strong>{perso && detection.personnages.length === 1 ? ` (niv. ${perso.level})` : ''}. Les objets lui seront envoyés.</>
            ) : (
              <><strong>Aucun personnage connecté détecté sur votre compte.</strong>{!confirmation && <><br />Connectez-vous en jeu avant de transférer. Déjà connecté ? Déconnectez-vous puis reconnectez votre personnage, puis cliquez sur Actualiser.</>}</>
            )}
          </span>
          <button type="button" style={styles.lien} disabled={chargement || enCours} onClick={actualiser}>Actualiser</button>
        </div>

        {message && (
          <div style={{ margin: '10px 22px 0', padding: '10px 12px', borderRadius: 8, fontSize: '0.84rem', lineHeight: 1.45, ...fondMessage[message.type] }}>
            {message.texte}
          </div>
        )}

        <div style={styles.corps}>
          {confirmation && confirmation.etape === 1 && (
            <div style={{ margin: '4px 0 10px', padding: '14px 16px', borderRadius: 10, background: 'rgba(255,179,0,0.07)', border: '1px solid rgba(255,179,0,0.45)', fontSize: '0.86rem', lineHeight: 1.55 }}>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Confirmation 1 sur 2</div>
              <h4 style={{ margin: '0 0 6px', color: '#FFD54F', fontSize: '0.95rem' }}>Aucun personnage connecté détecté</h4>
              Nous ne voyons aucun de vos personnages connecté en jeu. Avant d'envoyer {quoi} :
              <ul style={{ margin: '6px 0 10px 18px', padding: 0 }}>
                <li>vérifiez que vous êtes bien connecté en jeu avec votre personnage ;</li>
                <li>si vous l'êtes, déconnectez-vous puis reconnectez votre personnage, puis cliquez sur « Actualiser ».</li>
              </ul>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button type="button" style={bouton('secondaire', false)} onClick={() => setConfirmation(null)}>Annuler</button>
                <button type="button" style={bouton('secondaire', chargement)} disabled={chargement} onClick={actualiser}>Actualiser</button>
                <button type="button" style={bouton('danger', false)} onClick={() => { setConfirmation({ ...confirmation, etape: 2 }); setCaseCochee(false); }}>Je suis connecté, continuer</button>
              </div>
            </div>
          )}
          {confirmation && confirmation.etape === 2 && (
            <div style={{ margin: '4px 0 10px', padding: '14px 16px', borderRadius: 10, background: 'rgba(255,179,0,0.07)', border: '1px solid rgba(255,179,0,0.45)', fontSize: '0.86rem', lineHeight: 1.55 }}>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Confirmation 2 sur 2</div>
              <h4 style={{ margin: '0 0 6px', color: '#FFD54F', fontSize: '0.95rem' }}>Confirmer l'envoi sans personnage détecté ?</h4>
              L'envoi partira vers le personnage connecté sur votre compte. S'il n'y en a aucun, le transfert échouera et {quoi} restera dans votre inventaire : rien n'est perdu.
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '8px 0 12px', fontSize: '0.82rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={caseCochee} onChange={(e) => setCaseCochee(e.target.checked)} />
                J'ai vérifié que mon personnage est connecté en jeu.
              </label>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button type="button" style={bouton('secondaire', false)} onClick={() => setConfirmation(null)}>Annuler</button>
                <button
                  type="button"
                  disabled={!caseCochee || enCours}
                  style={bouton('danger', !caseCochee || enCours)}
                  onClick={() => { const d = confirmation; setConfirmation(null); envoyer(d, true); }}
                >
                  Confirmer le transfert
                </button>
              </div>
            </div>
          )}

          {livres.map((l) => (
            <div key={`livre-${l.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'rgba(102,187,106,0.08)', marginBottom: 8, borderLeft: '3px solid #66BB6A' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{l.itemName}</div>
                <div style={{ fontSize: '0.74rem', marginTop: 4, color: '#A5D6A7' }}>Livré{l.playerName ? ` à ${l.playerName}` : ''}. L'objet quitte l'inventaire.</div>
              </div>
            </div>
          ))}
          {chargement && lignes.length === 0 && livres.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 12px', color: 'rgba(255,255,255,0.45)' }}>Chargement de votre inventaire...</div>
          ) : lignes.length === 0 && livres.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 12px', color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem', lineHeight: 1.6 }}>
              <strong style={{ display: 'block', color: '#fff', fontSize: '1rem', marginBottom: 6 }}>Votre inventaire est vide.</strong>
              Vos prochains gains de la roue apparaîtront ici.<br />Vous pourrez ensuite les transférer en jeu.
            </div>
          ) : (
            lignes.map((l) => {
              const rarete = (raretes && raretes[l.itemId]) || '';
              const couleur = (couleurs && couleurs[rarete]) || COULEUR_DEFAUT;
              const icone = icones && icones[l.itemId];
              return (
                <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', marginBottom: 8, borderLeft: `3px solid ${couleur}` }}>
                  {icone ? (
                    <img src={icone} alt="" style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }} />
                  ) : (
                    <img src="/assets/images/titre.png" alt="" style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{l.itemName}</div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
                      {rarete && <span style={{ color: couleur }}>{rarete} - </span>}gagné le {dateCourte(l.requestedAt)}
                    </div>
                    {etatLigne(l)}
                  </div>
                  {boutonLigne(l)}
                </div>
              );
            })
          )}
        </div>

        {lignes.length > 0 && (
          <div style={styles.pied}>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={enCours || !!confirmation || transferables.length === 0}
                style={bouton('primaire', enCours || !!confirmation || transferables.length === 0)}
                onClick={() => transferer({ tout: true })}
              >
                {enCours ? 'Transfert en cours...' : `Tout transférer (${transferables.length})`}
              </button>
            </div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', textAlign: 'right' }}>
              Un seul clic suffit : un objet ne peut jamais être envoyé deux fois.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
